import { applyTaskSort } from '../listView/taskFilters';
import { visibleSections } from '../sections/types';
import type { Section } from '../sections/types';
import type { ProjectStatus } from '../projects/types';
import type { SavedViewSort } from '../savedViews/types';
import type { Task } from '../tasks/types';

/**
 * Board View grouping (handoff 18), as pure functions over the already-loaded
 * task list. Columns are the project's active statuses in position order; a
 * task whose status is unset, inactive or unknown belongs to the
 * system-generated Uncategorized column (handoff 15), which leads the board —
 * new tasks start there — and is hidden when empty.
 *
 * Inside a column, cards follow the toolbar's sort: the Board persists no
 * order of its own (locked Phase 5 decision).
 *
 * Batch 7 added a second grouping. Handoff 18 had sections as FILTERS on the
 * Board and statuses as the only columns; grouping by section makes the board
 * a second view of the same list rather than a different question about it.
 * The section filter stays — filtering to one section and grouping by section
 * are still different things, the first narrowing what you see and the second
 * arranging it.
 */

/**
 * What the board's columns stand for.
 *
 * 'swimlane' is both at once — sections down, statuses across — and is drawn
 * by SwimlaneBoard rather than by BoardView. It appears in this union because
 * it is a board arrangement and the toggle is one control; the drop handlers
 * for it take two ids rather than one, which is why it has its own component.
 */
export type BoardGrouping = 'status' | 'section' | 'swimlane';

export interface BoardColumn {
  /** Stable render key: the group id, or 'uncategorized'. */
  key: string;
  /** What a drop on this column writes. */
  grouping: BoardGrouping;
  /**
   * The Completed column of the unified board (buildUnifiedColumns). False
   * everywhere else, including on the ordinary status board — where the
   * project's completed-type status is just another column and completing a
   * task is not something the Board does at all.
   */
  isCompletedColumn?: boolean;
  /**
   * The status or section this column stands for. Null is the catch-all
   * column, and a drop there CLEARS the field rather than setting it — that
   * is what makes Uncategorized a way out of a status or a section rather
   * than a one-way door.
   */
  groupId: string | null;
  name: string;
  colorKey: string;
  /** Renaming is a section-only affordance; statuses have no CRUD. */
  canRename: boolean;
  tasks: Task[];
}

export const UNCATEGORIZED_COLUMN_NAME = 'Uncategorized';

export function buildBoardColumns(
  tasks: Task[],
  statuses: ProjectStatus[],
  sort: SavedViewSort,
): BoardColumn[] {
  const active = statuses
    .filter((status) => status.is_active)
    .slice()
    .sort((left, right) => left.position - right.position);
  const known = new Set(active.map((status) => status.id));

  const columns: BoardColumn[] = active.map((status) => ({
    key: status.id,
    grouping: 'status',
    groupId: status.id,
    name: status.name,
    colorKey: status.color_key,
    canRename: false,
    tasks: applyTaskSort(
      tasks.filter((task) => task.status_id === status.id),
      sort,
    ),
  }));

  const uncategorized = applyTaskSort(
    tasks.filter((task) => task.status_id === null || !known.has(task.status_id)),
    sort,
  );
  if (uncategorized.length > 0) {
    columns.unshift({
      key: 'uncategorized',
      grouping: 'status',
      groupId: null,
      name: UNCATEGORIZED_COLUMN_NAME,
      colorKey: 'neutral',
      canRename: false,
      tasks: uncategorized,
    });
  }

  return columns;
}

export const UNSECTIONED_COLUMN_NAME = 'No section';

/**
 * The same board, grouped by section.
 *
 * Two deliberate differences from the status version. The catch-all column is
 * ALWAYS shown, because it is where a new task lands and where a card has to
 * be draggable back out to — hiding it when empty would leave no target. And
 * every real section column can be renamed, because sections are the app's
 * own data and already have rename, create and reorder mutations; statuses do
 * not have any of that and are created by the database.
 */
export function buildSectionColumns(
  tasks: Task[],
  sections: Section[],
  sort: SavedViewSort,
): BoardColumn[] {
  const open = visibleSections(sections);
  const known = new Set(open.map((section) => section.id));

  const columns: BoardColumn[] = open.map((section) => ({
    key: section.id,
    grouping: 'section',
    groupId: section.id,
    name: section.name,
    colorKey: 'neutral',
    canRename: true,
    tasks: applyTaskSort(
      tasks.filter((task) => task.section_id === section.id),
      sort,
    ),
  }));

  // An archived section's tasks are not lost — they surface here, the same
  // way the List View drops them into Uncategorized.
  columns.unshift({
    key: 'unsectioned',
    grouping: 'section',
    groupId: null,
    name: UNSECTIONED_COLUMN_NAME,
    colorKey: 'neutral',
    canRename: false,
    tasks: applyTaskSort(
      tasks.filter((task) => task.section_id === null || !known.has(task.section_id)),
      sort,
    ),
  });

  return columns;
}

export const COMPLETED_COLUMN_NAME = 'Completed';

/**
 * The unified board: permanent status columns, and Completed always last.
 *
 * Personal Tasks only, for now. The flaw it fixes is that is_completed and
 * status_id are two independent fields — the checkbox in List View writes one,
 * the Board writes the other, and neither reads the other. So ticking a task
 * off dropped it into the List View's Completed drawer and left its card
 * sitting in "In Progress", two views of the same task disagreeing about
 * whether it was done.
 *
 * The fix is to stop treating them as separate questions HERE, where the
 * columns are decided, rather than with a database trigger that would change
 * how completion works for every project in the app.
 *
 * Three rules, in order:
 *
 *   Completion wins. A ticked task is in Completed whatever its status says.
 *   That is what makes the checkbox move the card without writing a status.
 *
 *   The project's completed-TYPE status is folded in rather than rendered.
 *   Every project seeds a status row literally called Completed; left alone,
 *   this board would show two columns with that name and no way to tell which
 *   one meant anything. Tasks parked on it — reachable from the old board,
 *   where dropping on that column set a status and ticked nothing — join the
 *   real Completed column.
 *
 *   The column exists even when empty, and even when the project has no
 *   completed-type status at all. It reflects a boolean on the task, not a row
 *   in project_statuses, so there is nothing for its absence to depend on.
 */
export function buildUnifiedColumns(
  tasks: Task[],
  statuses: ProjectStatus[],
  sort: SavedViewSort,
): BoardColumn[] {
  const active = statuses
    .filter((status) => status.is_active)
    .slice()
    .sort((left, right) => left.position - right.position);
  const completedStatus = active.find((status) => status.is_completed_status) ?? null;
  const working = active.filter((status) => !status.is_completed_status);
  const known = new Set(working.map((status) => status.id));

  const isDone = (task: Task): boolean =>
    task.is_completed || (completedStatus !== null && task.status_id === completedStatus.id);

  const open = tasks.filter((task) => !isDone(task));

  const columns: BoardColumn[] = working.map((status) => ({
    key: status.id,
    grouping: 'status',
    groupId: status.id,
    name: status.name,
    colorKey: status.color_key,
    canRename: false,
    isCompletedColumn: false,
    tasks: applyTaskSort(
      open.filter((task) => task.status_id === status.id),
      sort,
    ),
  }));

  const uncategorized = applyTaskSort(
    open.filter((task) => task.status_id === null || !known.has(task.status_id)),
    sort,
  );
  if (uncategorized.length > 0) {
    columns.unshift({
      key: 'uncategorized',
      grouping: 'status',
      groupId: null,
      name: UNCATEGORIZED_COLUMN_NAME,
      colorKey: 'neutral',
      canRename: false,
      isCompletedColumn: false,
      tasks: uncategorized,
    });
  }

  columns.push({
    key: 'completed',
    grouping: 'status',
    // The completed-type status when the project has one, so a drop writes a
    // status that agrees with the tick. Null when it does not, which simply
    // leaves the status alone.
    groupId: completedStatus?.id ?? null,
    name: COMPLETED_COLUMN_NAME,
    colorKey: completedStatus?.color_key ?? 'success',
    canRename: false,
    isCompletedColumn: true,
    tasks: applyTaskSort(tasks.filter(isDone), sort),
  });

  return columns;
}

/**
 * What a drop on a column writes: both fields, every time.
 *
 * Setting only the one the column obviously stands for is what allowed the two
 * to drift apart in the first place. Dropping on Completed ticks the task AND
 * files it under the completed status; dropping anywhere else sets that status
 * AND un-ticks it. After any board move the two fields agree, whatever state
 * the task arrived in.
 */
export function dropIntent(column: BoardColumn): { statusId: string | null; isCompleted: boolean } {
  return { statusId: column.groupId, isCompleted: column.isCompletedColumn === true };
}

export interface MoveOption {
  key: string;
  label: string;
  statusId: string | null;
  isCompleted: boolean;
}

/**
 * The card menu's targets on the unified board: every column but the one the
 * card is already in.
 *
 * Derived from the columns rather than from the status list, because on this
 * board Completed is a column with no status of its own to enumerate — and
 * because working out which column a card is in by re-deriving the rules here
 * is how the menu and the columns would eventually come to disagree. It asks
 * the columns directly.
 *
 * The menu is not a convenience. It is the only way to move a card without a
 * mouse, so anything the board can do by dragging has to be here too.
 */
export function unifiedMoveOptions(task: Task, columns: BoardColumn[]): MoveOption[] {
  const current = columns.find((column) => column.tasks.some((row) => row.id === task.id));
  return columns
    .filter((column) => column.key !== current?.key)
    .map((column) => ({
      key: column.key,
      label: `Move to ${column.name}`,
      ...dropIntent(column),
    }));
}

/** Subtask progress for the card indicator: "done of total" (handoff 20). */
export interface SubtaskProgress {
  done: number;
  total: number;
}

export function subtaskProgress(
  subtasksByParent: Record<string, Task[]>,
): Record<string, SubtaskProgress> {
  const progress: Record<string, SubtaskProgress> = {};
  for (const [parentId, rows] of Object.entries(subtasksByParent)) {
    progress[parentId] = {
      done: rows.filter((row) => row.is_completed).length,
      total: rows.length,
    };
  }
  return progress;
}
